// src/features/system/developer/components/video-gallery/LiveTimeAgo.tsx
// ────────────────────────────────────────────────────────────────────────────
// Precise, self-updating "time ago" pill.
//
//   • Format: `1d 3h 45m 15s ago`, `3h 45m 15s ago`, `45m 15s ago`, `15s ago`
//     (zero-leading components are dropped). Always relative time.
//
//   • Performance: a single process-wide `setInterval(1000ms)` drives **all**
//     instances. Each mounted instance subscribes to a shared listener Set
//     and triggers ONLY its own local `useState` setter — so the parent card
//     never re-renders. With 200 visible cards this costs 200 tiny `<span>`
//     re-renders per second instead of 200 full card subtrees.
//
//   • The ticker self-stops when the listener Set is empty so the gallery
//     unmounting fully releases the timer (no idle drift, no background CPU).
//
//   • Subscribers are notified on `document.visibilitychange` so tabs in the
//     background do not run a 1Hz loop, and the UI snaps to the correct value
//     the moment the tab becomes visible again.
// ────────────────────────────────────────────────────────────────────────────
import React, { memo, useEffect, useState } from 'react';

/** Render a precise "1d 3h 45m 15s ago" label that updates every second. */
function LiveTimeAgoImpl({
  ms,
  className,
  title,
}: {
  ms: number;
  className?: string;
  title?: string;
}) {
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    const listener = () => setNow(Date.now());
    addListener(listener);
    return () => removeListener(listener);
  }, []);

  return (
    <span className={className} title={title}>
      {formatTimeAgoPrecise(ms, now)}
    </span>
  );
}

export default memo(LiveTimeAgoImpl);

// ── Singleton 1Hz scheduler ────────────────────────────────────────────────
type Listener = () => void;
const listeners = new Set<Listener>();
let timerId: ReturnType<typeof setInterval> | null = null;
let visListener: (() => void) | null = null;

function startTimerIfNeeded() {
  if (timerId !== null) return;
  // Skip ticking when the document is hidden — the visibilitychange handler
  // will fire a single catch-up tick when the tab becomes visible again.
  timerId = setInterval(() => {
    if (typeof document !== 'undefined' && document.hidden) return;
    listeners.forEach((l) => l());
  }, 1000);

  if (typeof document !== 'undefined' && visListener === null) {
    visListener = () => {
      if (!document.hidden) listeners.forEach((l) => l());
    };
    document.addEventListener('visibilitychange', visListener);
  }
}

function stopTimerIfIdle() {
  if (listeners.size > 0) return;
  if (timerId !== null) {
    clearInterval(timerId);
    timerId = null;
  }
  if (visListener !== null && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', visListener);
    visListener = null;
  }
}

function addListener(l: Listener) {
  listeners.add(l);
  startTimerIfNeeded();
}

function removeListener(l: Listener) {
  listeners.delete(l);
  stopTimerIfIdle();
}

// ── Precise formatter ──────────────────────────────────────────────────────
/**
 * Returns "Xd Yh Zm Ws ago" with zero-leading components dropped.
 *   • diff <= 0           → "just now"
 *   • diff < 1m           → "12s ago"
 *   • diff < 1h           → "4m 15s ago"
 *   • diff < 1d           → "3h 45m 15s ago"
 *   • diff >= 7d          → "8d 3h 45m 15s ago"
 */
export function formatTimeAgoPrecise(ms: number, now = Date.now()): string {
  if (!ms || ms <= 0) return '—';
  const diffSec = Math.floor((now - ms) / 1000);
  if (diffSec <= 0) return 'just now';

  const day = Math.floor(diffSec / 86_400);
  const hr  = Math.floor((diffSec % 86_400) / 3_600);
  const min = Math.floor((diffSec % 3_600) / 60);
  const sec = diffSec % 60;

  // Drop zero-leading components but keep the trailing seconds so the label
  // visibly ticks even when the user has been staring at a 3-hour-old card.
  const parts: string[] = [];
  if (day > 0) parts.push(`${day}d`);
  if (day > 0 || hr  > 0) parts.push(`${hr}h`);
  if (day > 0 || hr  > 0 || min > 0) parts.push(`${min}m`);
  parts.push(`${sec}s`);
  return `${parts.join(' ')} ago`;
}
